import type { FileType, FileUploadPropsType } from 'app/src/model'
import { useWebAppContext } from 'app/src/shared/hooks'
import prettyBytes from 'pretty-bytes'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { FileUpload, type FileUploadFile, type FileUploadHandlerEvent, type ItemTemplateOptions } from 'primereact/fileupload'
import { Message } from 'primereact/message'
import { useEffect, useRef, useState } from 'react'

export function FileUploader(props: FileUploadPropsType) {
  const context = useWebAppContext()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [disabled, setDisabled] = useState<boolean>()
  const fileUploadRef = useRef<FileUpload>(null)

  useEffect(() => {
    if (props.file?.documentUri) preloadFile(props.file?.documentName, props.file?.documentUri)
    if (props.file) setDisabled(true)
  }, [props.file])

  const onDeleteFile = (_file: unknown, event: React.SyntheticEvent, callback: (event: React.SyntheticEvent) => void) => {
    props.onFileStateChange!()
    setDisabled(false)
    callback(event) //necessario per l'eliminazione grafica
  }
  const handleOnFileChange = (evt: FileUploadHandlerEvent) => {
    setDisabled(true)
    setErrorMessage(undefined)
    if (evt.files && evt.files.length > 0) {
      const file = evt.files[0]

      if (file.size > props.maxUploadFileSize) {
        setErrorMessage(`${props.errorFileSizeNotSupported}`)
        setDisabled(false)
        return
      }
      if (!props.acceptTypes?.split(',')?.some((x) => x.trim() === file.type)) {
        setErrorMessage(`${props.errorFileTypeNotSupported}`)
        setDisabled(false)
        return
      }

      const currUpload: FileType = {
        documentName: file.name,
        documentSize: file.size,
        documentMimeType: file.type,
      }

      const fileReader = new FileReader()

      fileReader.onload = function () {
        try {
          const result = fileReader.result
          props.onFileStateChange!({
            ...currUpload,
            blob: new Blob([result!]),
          })
        } catch {
          setErrorMessage(props.errorFileLoading)
          setDisabled(false)
        }
      }
      try {
        fileReader.readAsArrayBuffer(file)
      } catch {
        setErrorMessage(props.errorFileLoading)
        setDisabled(false)
      }
    }
  }

  const preloadFile = (fileName: string, fileUri: string) => {
    const file = new File([], fileName, { type: 'text/plain' })
    if (fileUploadRef.current) {
      fileUploadRef.current.getFiles().push({ ...file, name: fileName, objectURL: fileUri })
    }
  }

  const handleFileDownload = async (file: FileUploadFile) => {
    let httpResp
    if (props.file?.blob && props.file?.documentUri)
      httpResp = await context?.httpClient?.downloadFile({ input: props.file?.documentUri ?? '' }, props.file?.documentName ?? '')
    else httpResp = await context?.httpClient?.downloadFile({ input: file.objectURL ?? '' }, file.name)

    if (httpResp?.fetchError || httpResp?.blobError || !httpResp?.httpResponse || !httpResp.httpResponse.ok) {
      setErrorMessage(props.errorFileDownload)
    }
  }

  const itemTemplate = (file: object, options: ItemTemplateOptions): React.ReactNode => {
    const fileCast = file as FileUploadFile
    if (errorMessage) return <></>
    else
      return (
        <div className='grid grid-cols-12 p-0'>
          <div className='col-span-12 col-start-1 grid lg:col-span-6 lg:col-start-1'>
            <Button
              label={fileCast.name}
              text
              className='mt-[5px] pl-[10px] text-left'
              icon='pi pi-file'
              iconPos='left'
              pt={{
                label: { className: 'text-[16px] text-[#323130] underline font-normal leading-[22px]' },
                icon: { className: 'text-[16px]' },
              }}
              onClick={() => handleFileDownload(fileCast)}
            />
          </div>
          <div className='col-span-11 col-start-2 grid lg:col-span-3 lg:col-start-10'>
            <div className='align-items-end flex flex-row items-center'>
              {props.file?.documentSize && <p>{prettyBytes(props.file?.documentSize)} </p>}
              <Button label={props.deleteButtonLabel} link onClick={(evt) => onDeleteFile(file, evt, options.onRemove)} />
            </div>
          </div>
        </div>
      )
  }

  const emptyTemplate = () => {
    return <p className='m-0 p-0 text-center'>{props.uploadAreaLabel}</p>
  }

  return (
    <div className='mt-[40px]'>
      <p className='text-[16px] font-bold'>{props.title}</p>
      <p className='mt-[10px] text-[14px] font-normal'>{props.description}</p>
      {props.templateBtnFileName && props.templateBtnFileLink && (
        <Button
          label={props.templateBtnLabel}
          text
          className='mt-[5px] pl-[10px] text-left'
          icon='pi pi-file-pdf'
          iconPos='left'
          pt={{
            label: { className: 'text-[16px] text-[#323130] underline font-normal leading-[22px]' },
            icon: { className: 'text-[16px]' },
          }}
          onClick={async () => {
            const httpResp = await context?.httpClient?.downloadFile(
              { input: props.templateBtnFileLink! },
              props.templateBtnFileName!,
            )
            if (httpResp?.fetchError || httpResp?.blobError || !httpResp?.httpResponse || !httpResp.httpResponse.ok) {
              setErrorMessage(props.errorFileDownload)
            }
          }}
        />
      )}
      <div className='card justify-content-center mt-[30px] flex'>
        <Message
          severity='info'
          pt={{
            text: { className: 'text-[#323130] text-[16px] bg-[#F2F8FF] leading-[20px]' },
            icon: { className: 'text-[#323130]' },
          }}
          text={`${props.supportedFilesLabel} ${props.acceptExtensions}. ${props.supportedSizeLabel} ${prettyBytes(props.maxUploadFileSize)}`}
        />
      </div>
      <div className='card my-[15px]'>
        <FileUpload
          ref={fileUploadRef}
          mode='advanced'
          accept={props.acceptExtensions}
          customUpload
          disabled={disabled}
          uploadHandler={handleOnFileChange}
          auto
          pt={{
            basicButton: { className: 'bg-white text-[#0047bb] border-0 text-[16px] px-[8px]' },
            chooseButton: {
              className: 'bg-white text-[#0047bb] border-0 text-[16px] px-[8px]',
              tabIndex: disabled ? -1 : 0,
            },
            label: { className: 'font-bold' },
            chooseButtonLabel: { className: 'font-bold' },
            chooseIcon: { className: ' text-[26px]' },
          }}
          chooseLabel={props.uploadButtonLabel}
          chooseOptions={{ icon: 'pi pi-upload' }}
          emptyTemplate={emptyTemplate}
          itemTemplate={itemTemplate}
        />
      </div>
      {errorMessage && <p className='text-center text-[16px] text-red-500'>{errorMessage}</p>}
      <Divider className='border-t-[1px] border-[#605e5c]' />
    </div>
  )
}
